import _taggedTemplateLiteral from "@babel/runtime/helpers/taggedTemplateLiteral";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
var _templateObject;
/* eslint-disable @atlaskit/design-system/prefer-primitives, @atlaskit/design-system/consistent-css-prop-usage */
/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag
 */

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { jsx, css } from '@emotion/react';
import React, { useState } from 'react';
import { WidthConsumer } from '@atlaskit/editor-common/ui';
import { RendererCssClassName } from '../../consts';
import { calcBreakoutWidth } from '@atlaskit/editor-common/utils';
import { useMultiBodiedExtensionActions } from './multiBodiedExtension/actions';
import { useMultiBodiedExtensionContext } from './multiBodiedExtension/context';
var containerStyles = css({
  // Remove top margin if MBE is the first on the doc/page (MBE can only be on the first level)
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-nested-selectors
  '&:first-child > .ak-renderer-extension': {
    marginTop: 0
  }
});
var MultiBodiedExtensionChildrenContainer = function MultiBodiedExtensionChildrenContainer(_ref) {
  var children = _ref.children;
  return jsx("article", {
    "data-testid": "multiBodiedExtension--frames"
  }, children);
};
var MultiBodiedExtensionNavigation = function MultiBodiedExtensionNavigation(_ref2) {
  var children = _ref2.children;
  return jsx("nav", {
    "data-testid": "multiBodiedExtension-navigation"
  }, children);
};
var MultiBodiedExtensionWrapper = function MultiBodiedExtensionWrapper(_ref3) {
  var width = _ref3.width,
    path = _ref3.path,
    layout = _ref3.layout,
    children = _ref3.children;
  var isTopLevel = path.length < 1;
  var centerAlignClass = isTopLevel && ['wide', 'full-width'].includes(layout) ? RendererCssClassName.EXTENSION_CENTER_ALIGN : '';

  // This hierarchy is copied from regular extension (see extension.tsx)
  return jsx("div", {
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
    className: "".concat(RendererCssClassName.EXTENSION, " ").concat(centerAlignClass),
    style: {
      // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
      width: isTopLevel ? calcBreakoutWidth(layout, width) : '100%'
    },
    "data-layout": layout,
    "data-testid": "multiBodiedExtension--wrapper"
  }, jsx("div", {
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
    className: "".concat(RendererCssClassName.EXTENSION_OVERFLOW_CONTAINER)
  }, children));
};
var MultiBodiedExtension = function MultiBodiedExtension(props) {
  var _extensionContext$pri;
  var children = props.children,
    _props$layout = props.layout,
    layout = _props$layout === void 0 ? 'default' : _props$layout,
    _props$path = props.path,
    path = _props$path === void 0 ? [] : _props$path,
    parameters = props.parameters,
    extensionType = props.extensionType,
    extensionKey = props.extensionKey,
    content = props.content,
    marks = props.marks,
    localId = props.localId;
  var _useState = useState(0),
    _useState2 = _slicedToArray(_useState, 2),
    activeChildIndex = _useState2[0],
    setActiveChildIndex = _useState2[1];
  var _useMultiBodiedExtens = useMultiBodiedExtensionContext({
      extensionType: extensionType,
      extensionKey: extensionKey
    }),
    loading = _useMultiBodiedExtens.loading,
    extensionContext = _useMultiBodiedExtens.extensionContext;
  var actions = useMultiBodiedExtensionActions({
    updateActiveChild: setActiveChildIndex,
    children: children,
    childrenContainer: jsx(MultiBodiedExtensionChildrenContainer, null, children),
    allowBodiedOverride: extensionContext === null || extensionContext === void 0 || (_extensionContext$pri = extensionContext.privateProps) === null || _extensionContext$pri === void 0 ? void 0 : _extensionContext$pri.__allowBodiedOverride
  });
  var renderContent = React.useCallback(function () {
    var _marks$find;
    if (loading || !extensionContext) {
      return null;
    }
    var NodeRenderer = extensionContext.NodeRenderer,
      privateProps = extensionContext.privateProps;
    var fragmentLocalId = marks === null || marks === void 0 || (_marks$find = marks.find(function (m) {
      return m.type.name === 'fragment';
    })) === null || _marks$find === void 0 || (_marks$find = _marks$find.attrs) === null || _marks$find === void 0 ? void 0 : _marks$find.localId;
    var node = {
      type: 'multiBodiedExtension',
      extensionKey: extensionKey,
      extensionType: extensionType,
      parameters: parameters,
      content: content,
      localId: localId,
      fragmentLocalId: fragmentLocalId
    };
    if (privateProps.__allowBodiedOverride) {
      return jsx(NodeRenderer, {
        node: node,
        actions: actions
      });
    } else {
      return jsx(React.Fragment, null, jsx(MultiBodiedExtensionNavigation, null, jsx(NodeRenderer, {
        node: node,
        actions: actions
      })), jsx(MultiBodiedExtensionChildrenContainer, null, children));
    }
  }, [loading, extensionContext, marks, extensionKey, extensionType, parameters, content, localId, actions, children]);

  // make the frame visible
  // eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression
  var containerActiveFrameStyles = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n\t\t& [data-extension-frame='true']:nth-of-type(", ") {\n\t\t\tdisplay: block;\n\t\t}\n\t"])), activeChildIndex + 1);
  return jsx("section", {
    css: [containerStyles, containerActiveFrameStyles],
    "data-testid": "multiBodiedExtension--container",
    "data-multiBodiedExtension-container": true,
    "data-active-child-index": activeChildIndex,
    "data-layout": layout,
    "data-local-id": localId
  }, jsx(WidthConsumer, null, function (_ref4) {
    var width = _ref4.width;
    return jsx(MultiBodiedExtensionWrapper, {
      layout: layout,
      width: width,
      path: path
    }, renderContent());
  }));
};
export default MultiBodiedExtension;